import Component from '@glimmer/component';
import { action, setProperties, get } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { dropTask } from 'ember-concurrency';
import { service } from '@ember/service';
import { DESK_ICON, SELECTED_GROUP_DESK_ICON } from 'garaje/utils/map/map-icons';

/**
 * GroupDeskReservationMap Component
 *
 * Displays a floor map for selecting multiple desks for a group reservation.
 * Adapted from ReservationModalMap but designed for multi-desk, single-day selection.
 *
 * @param {Array<Desk>}     availableDesks      Pre-filtered available desks from Phase 1
 * @param {Array<Floor>}    availableFloors     Floors extracted from availableDesks
 * @param {Array<Employee>} selectedCollaborators  Employees who need desks
 * @param {Date}            selectedDate        The reservation date
 * @param {Array<Desk>}     selectedDesks       Currently selected desks (managed by parent)
 * @param {Function}        onDeskToggle        Callback when desk is clicked: (desk) => void
 * @param {Function}        onFloorChange       Callback when floor is changed: () => void
 */

export default class GroupDeskReservationMapComponent extends Component {
  @service state;

  @tracked isMapReady = false;
  @tracked mapHeight;
  @tracked mapWidth;
  @tracked currentFloor;

  /**
   * Filter to only floors that have floor plans
   */
  get floorsWithMap() {
    if (!this.args.availableFloors) {
      return [];
    }
    return this.args.availableFloors.filter((floor) => get(floor, 'floorPlanUrl'));
  }

  /**
   * Get desks on the currently selected floor that are placed on the map
   */
  get placedDesks() {
    if (!this.currentFloor) {
      return [];
    }
    if (!this.args.availableDesks || this.args.availableDesks.length === 0) {
      return [];
    }
    const currentFloorId = get(this.currentFloor, 'id');
    const filtered = this.args.availableDesks.filter((desk) => {
      const deskFloorId = get(desk, 'floor.id');
      const placed = get(desk, 'placed');
      const isDeleted = get(desk, 'isDeleted');
      return deskFloorId === currentFloorId && placed && !isDeleted;
    });
    return filtered;
  }

  /**
   * Apply icons to desks based on selection state
   */
  get placedDesksWithIcons() {
    const selectedDeskIds = new Set(this.args.selectedDesks.map((d) => get(d, 'id')));

    return this.placedDesks.map((desk) => {
      const deskId = get(desk, 'id');
      const isSelected = selectedDeskIds.has(deskId);

      if (isSelected) {
        this.setDeskIcon(desk, SELECTED_GROUP_DESK_ICON);
      } else {
        this.setDeskIcon(desk, DESK_ICON);
      }
      return desk;
    });
  }

  /**
   * Set icon properties on a desk object
   */
  setDeskIcon(desk, icon) {
    setProperties(desk, {
      iconClassname: icon.iconClassname,
      iconSize: icon.iconSize,
      iconHtml: '',
    });
  }

  /**
   * Load map image when component is inserted
   */
  @dropTask
  *onModalLoad() {
    // Set initial floor to first floor with map
    if (this.floorsWithMap.length > 0) {
      this.currentFloor = this.floorsWithMap[0];
    }

    yield this.loadMapImageTask.perform();
  }

  /**
   * Load the floor plan image and get its dimensions
   */
  @dropTask
  *loadMapImageTask() {
    if (!this.currentFloor) {
      return;
    }
    const floorPlanUrl = get(this.currentFloor, 'floorPlanUrl');
    if (!floorPlanUrl) {
      return;
    }

    try {
      yield new Promise((resolve, reject) => {
        const image = new Image();
        image.src = floorPlanUrl;
        image.onload = (ev) => {
          this.mapHeight = ev.target.height;
          this.mapWidth = ev.target.width;
          this.isMapReady = true;
          resolve();
        };
        image.onerror = (error) => {
          reject(error);
        };
      });
    } catch (error) {
      // Error already logged, silently fail
    }
  }

  /**
   * Handle floor change - reload map image for new floor and clear selections
   */
  @dropTask
  *onFloorChange(floor) {
    // Reset map ready state to prevent rendering with mismatched dimensions
    this.isMapReady = false;
    this.currentFloor = floor;
    // Clear selected desks when switching floors
    if (this.args.onFloorChange) {
      this.args.onFloorChange();
    }
    yield this.loadMapImageTask.perform();
  }

  /**
   * Set map ready state
   */
  @action
  toggleIsMapReady(bool) {
    this.isMapReady = bool;
  }
}
